import React from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { useHistory, useLocation } from 'react-router-dom'
import { AppPage, UnderlineTabs, colors } from 'src/components/app-ui'
import WorkspaceMembersPage from 'src/pages/workspace-members'
import CustomDomainTab from './custom-domain'
import BrandingTab from './branding'
import LanguageTab from './language-tab'
import IntegrationsTab from './integrations'
import SettingsTab from './settings-tab'
import { WORKSPACE_TABS, tabFromPath, workspacePath } from './tabs'

const TAB_CONTENT = {
  branding: BrandingTab,
  members: WorkspaceMembersPage,
  'custom-domain': CustomDomainTab,
  language: LanguageTab,
  integrations: IntegrationsTab,
  settings: SettingsTab,
}

const useStyles = makeStyles({
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.25,
    color: colors.navy,
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: '14px',
    color: colors.muted,
  },
  noAccess: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '80px 16px',
    textAlign: 'center',
    color: colors.muted,
  },
  noAccessTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: colors.navy,
  },
})

// Workspace roles that can't manage the workspace (same rule as the
// workspace switcher). Members checks its own access with the server.
const READ_ONLY_PRESETS = ['Viewer', 'Editor']

/** Same as Members when the server refuses it. */
const NoAccess = ({ classes }) => (
  <div className={classes.noAccess}>
    <Icon icon="tabler:lock" width={48} height={48} color={colors.subtle} />
    <div className={classes.noAccessTitle}>
      You don't have access to this page
    </div>
    <div>Contact your workspace owner to get the required permissions.</div>
  </div>
)

/**
 * Workspace section: tabs above the page head, one tab per area.
 * Also serves the legacy /workspace/members, /workspace/webhooks,
 * /workspace/api-keys, /custom-domain and /automations URLs.
 */
const WorkspacePage = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()

  const active = tabFromPath(location.pathname)
  const tab = WORKSPACE_TABS.find(item => item.value === active)
  const Content = TAB_CONTENT[active]
  const me = useSelector(state => state.authentication?.me)
  // Every plan sees the Members tab; plans without team seats get the upgrade panel there.
  const tabs = WORKSPACE_TABS
  const noAccess =
    active !== 'members' && READ_ONLY_PRESETS.includes(me?.permissionPreset)

  return (
    <AppPage>
      <UnderlineTabs
        tabs={tabs}
        value={active}
        onChange={value => history.push(workspacePath(value))}
      />
      {!tab.ownHead && (
        <div>
          <h1 className={classes.title}>{tab.title}</h1>
          <p className={classes.subtitle}>{tab.subtitle}</p>
        </div>
      )}
      {noAccess ? <NoAccess classes={classes} /> : <Content head={tab} />}
    </AppPage>
  )
}

export default WorkspacePage
